import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import WhatsAppDevicePreview from '../WhatsApp/WhatsAppDevicePreview';
import WhatsAppSendVariableEditor from '../WhatsApp/WhatsAppSendVariableEditor';
import WhatsAppSendButtonLinkEditor, {
  applySendButtonPreview,
  defaultSendButtonSlots,
  sendButtonSlotsToButtonValues,
  type SendButtonLinkSlot,
} from '../WhatsApp/WhatsAppSendButtonLinkEditor';
import WhatsAppTemplateSearchSelect from '../WhatsApp/WhatsAppTemplateSearchSelect';
import { devicePreviewFromTemplate } from '../WhatsApp/whatsappTemplatePreview';
import { buildPreviewMessage, inferVariableLabels, normalizeContactPhoneInput } from '../WhatsApp/whatsappPreview';
import { applyDoctorHonorificDedupeToBodyValues } from '../WhatsApp/whatsappDoctorHonorific';
import { parseConsecutiveTemplateSlots } from '../WhatsApp/whatsappTemplateSlots';
import {
  defaultSendSlots,
  getSendDeepLinkContextFields,
  resolveSendPreviewValues,
  sendSlotToBodyValue,
  sendSlotsAreValid,
  type SendTemplateVariableSlot,
} from '../WhatsApp/whatsappSendRecipientFields';
import { formatDisplayPhone } from '../WhatsApp/whatsappUi';
import type { WhatsAppSendRecipient } from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';
import type { DoctorClinic } from '../../models/MasterAdmin/doctorClinic';
import {
  fetchApprovedWhatsAppTemplates,
  sendInboxMessage,
} from '../../services/MasterAdmin/whatsappCampaignService';
import { fetchDeepLinkCampaigns } from '../../services/MasterAdmin/deepLinkService';
import { formatDoctorName, formatDoctorSpecialities } from '../../utils/doctorName';

function buildVendorRecipient(clinic: DoctorClinic): WhatsAppSendRecipient | null {
  const parsed = normalizeContactPhoneInput(clinic.phoneNumber || '');
  if (!parsed) return null;
  const displayName =
    formatDoctorName(clinic.doctorName || '') ||
    clinic.clinicName?.trim() ||
    'Doctor';
  return {
    key: `${parsed.countryCode}|${parsed.phoneNumber}`,
    countryCode: parsed.countryCode,
    phoneNumber: parsed.phoneNumber,
    displayName,
    maskedPhone: formatDisplayPhone(parsed.countryCode, parsed.phoneNumber),
    recipientType: 'vendor',
    recipientId: clinic.vendorId,
    source: 'vendor',
    vendorKind: 'doctor-clinic',
    email: clinic.email || null,
    city: clinic.city || null,
    tags: [],
  };
}

type Props = {
  clinic: DoctorClinic;
  open: boolean;
  onClose: () => void;
};

const DoctorClinicWhatsAppSendModal: React.FC<Props> = ({ clinic, open, onClose }) => {
  const recipient = useMemo(() => buildVendorRecipient(clinic), [clinic]);
  const specialtyText = useMemo(
    () => formatDoctorSpecialities(clinic.specializations || []) || '',
    [clinic.specializations]
  );

  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [deepLinkCampaigns, setDeepLinkCampaigns] = useState<DeepLinkCampaign[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [languageCode, setLanguageCode] = useState('en');
  const [variableSlots, setVariableSlots] = useState<SendTemplateVariableSlot[]>([]);
  const [buttonSlots, setButtonSlots] = useState<SendButtonLinkSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const campaignsById = useMemo(
    () => new Map(deepLinkCampaigns.map((c) => [c.id, c])),
    [deepLinkCampaigns]
  );

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.name === templateName),
    [templates, templateName]
  );

  const slotCount = useMemo(() => {
    const fromBody = parseConsecutiveTemplateSlots(selectedTemplate?.bodyPreview || '').count;
    return Math.max(fromBody, selectedTemplate?.variableCount ?? 0);
  }, [selectedTemplate]);

  const variableLabels = useMemo(
    () => inferVariableLabels(selectedTemplate?.bodyPreview || '', slotCount),
    [selectedTemplate, slotCount]
  );

  const recipients = useMemo(() => (recipient ? [recipient] : []), [recipient]);
  const contextFields = useMemo(() => getSendDeepLinkContextFields(recipients), [recipients]);

  const previewValues = useMemo(() => {
    if (!recipient) return variableSlots.map((s) => s.staticValue || '');
    return resolveSendPreviewValues(variableSlots, recipient, campaignsById);
  }, [variableSlots, recipient, campaignsById]);

  const previewMessage = useMemo(
    () => buildPreviewMessage(selectedTemplate?.bodyPreview || '', previewValues),
    [selectedTemplate, previewValues]
  );

  const devicePreviewProps = useMemo(() => {
    if (!selectedTemplate) {
      return {
        message: 'Select a template to preview',
        headerText: null as string | null,
        footer: null as string | null,
        buttons: [] as import('../../models/MasterAdmin/whatsappCampaign').WhatsAppTemplateButton[],
        headerMediaUrl: null as string | null,
        headerMimeType: null as string | null,
      };
    }
    const preview = devicePreviewFromTemplate(selectedTemplate);
    return {
      ...preview,
      message: previewMessage || selectedTemplate.bodyPreview || '',
      buttons: applySendButtonPreview(preview.buttons, buttonSlots, campaignsById),
    };
  }, [selectedTemplate, previewMessage, buttonSlots, campaignsById]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setTemplateName('');
    setVariableSlots([]);
    setButtonSlots([]);
    Promise.all([
      fetchApprovedWhatsAppTemplates().then((r) => setTemplates(r.data || [])),
      fetchDeepLinkCampaigns()
        .then(setDeepLinkCampaigns)
        .catch(() => setDeepLinkCampaigns([])),
    ]).finally(() => setLoading(false));
  }, [open]);

  const handleTemplateChange = (name: string) => {
    setTemplateName(name);
    const tpl = templates.find((t) => t.name === name);
    setLanguageCode(tpl?.languageCode || 'en');
    const fromBody = parseConsecutiveTemplateSlots(tpl?.bodyPreview || '').count;
    const count = Math.max(fromBody, tpl?.variableCount ?? 0);
    setVariableSlots(defaultSendSlots(count, tpl?.defaultVariableMappings));
    setButtonSlots(defaultSendButtonSlots(tpl));
  };

  const fillStaticSlot = (slot: number, value: string) => {
    setVariableSlots((prev) =>
      prev.map((s) =>
        s.slot === slot
          ? { ...s, sourceType: 'static', staticValue: value, fieldKey: null, deepLinkCampaignId: null }
          : s
      )
    );
  };

  const quickFills = useMemo(() => {
    const items: Array<{ label: string; value: string }> = [];
    const doctorName = formatDoctorName(clinic.doctorName || '');
    if (doctorName) items.push({ label: 'Doctor name', value: doctorName });
    if (clinic.clinicName?.trim()) items.push({ label: 'Clinic name', value: clinic.clinicName.trim() });
    if (specialtyText) items.push({ label: 'Specialty', value: specialtyText });
    if (clinic.city?.trim()) items.push({ label: 'City', value: clinic.city.trim() });
    if (recipient) {
      items.push({
        label: 'Phone',
        value: formatDisplayPhone(recipient.countryCode, recipient.phoneNumber),
      });
    }
    return items;
  }, [clinic, specialtyText, recipient]);

  const variablesValid = variableSlots.length === 0 || sendSlotsAreValid(variableSlots);
  const canSend = Boolean(recipient && templateName && variablesValid && !sending);

  const handleSend = async () => {
    if (!recipient || !templateName) return;
    const bodyValues = variableSlots.map(sendSlotToBodyValue);
    const cleanBodyValues = applyDoctorHonorificDedupeToBodyValues({
      bodyPreview: selectedTemplate?.bodyPreview,
      slots: variableSlots,
      values: bodyValues,
    });

    setSending(true);
    try {
      await sendInboxMessage({
        countryCode: recipient.countryCode,
        phoneNumber: recipient.phoneNumber,
        contactName: recipient.displayName,
        email: recipient.email,
        city: recipient.city,
        vendorKind: recipient.vendorKind,
        recipientType: 'vendor',
        recipientId: recipient.recipientId,
        templateName,
        languageCode,
        bodyValues: cleanBodyValues,
        buttonValues: sendButtonSlotsToButtonValues(buttonSlots, selectedTemplate),
      });
      toast.success('WhatsApp message sent');
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to send WhatsApp'));
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex shrink-0 items-start justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Send WhatsApp</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              To {recipient?.displayName || 'doctor'}
              {recipient
                ? ` · ${formatDisplayPhone(recipient.countryCode, recipient.phoneNumber)}`
                : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {!recipient ? (
          <div className="p-6">
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              This doctor clinic has no valid phone number. Add a phone on the vendor profile first.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 rounded-lg border border-gray-200 px-4 py-2 text-sm dark:border-gray-600"
            >
              Close
            </button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-gray-500">Loading templates…</div>
        ) : (
          <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[1fr_300px]">
            <div className="space-y-4 overflow-y-auto p-5">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
                  Interakt template
                </label>
                <WhatsAppTemplateSearchSelect
                  templates={templates}
                  value={templateName}
                  onChange={handleTemplateChange}
                  placeholder="Search template…"
                  emptyLabel="Select template…"
                />
              </div>

              {selectedTemplate?.bodyPreview ? (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase text-gray-400">Template body</p>
                  <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-800">
                    {selectedTemplate.bodyPreview}
                  </p>
                </div>
              ) : null}

              {templateName && variableSlots.length > 0 ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Fill template variables
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Map each {'{{n}}'} to a notification field, static text, or deep link.
                    </p>
                  </div>

                  {quickFills.length > 0 ? (
                    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                      <p className="mb-2 text-xs font-medium text-gray-500">Quick fill into a variable</p>
                      <div className="flex flex-wrap gap-2">
                        {quickFills.map((item) => (
                          <div key={item.label} className="flex items-center gap-1">
                            <span className="text-[11px] text-gray-500">{item.label}:</span>
                            {variableSlots.map((slot) => (
                              <button
                                key={`${item.label}-${slot.slot}`}
                                type="button"
                                onClick={() => fillStaticSlot(slot.slot, item.value)}
                                className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] hover:border-[#25D366] hover:text-[#128C7E] dark:border-gray-600"
                                title={`Set {{${slot.slot}}} = ${item.value}`}
                              >
                                {`{{${slot.slot}}}`}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <WhatsAppSendVariableEditor
                    slots={variableSlots}
                    variableLabels={variableLabels}
                    recipients={recipients}
                    contextFields={contextFields}
                    campaigns={deepLinkCampaigns}
                    onCampaignCreated={(c) =>
                      setDeepLinkCampaigns((prev) => (prev.some((x) => x.id === c.id) ? prev : [c, ...prev]))
                    }
                    onChange={setVariableSlots}
                    disabled={sending}
                  />
                </div>
              ) : templateName ? (
                <p className="text-sm text-gray-500">This template has no body variables.</p>
              ) : null}

              {selectedTemplate ? (
                <WhatsAppSendButtonLinkEditor
                  template={selectedTemplate}
                  slots={buttonSlots}
                  onChange={setButtonSlots}
                  campaigns={deepLinkCampaigns}
                  contextFields={contextFields}
                  onCampaignCreated={(c) =>
                    setDeepLinkCampaigns((prev) => (prev.some((x) => x.id === c.id) ? prev : [c, ...prev]))
                  }
                  disabled={sending}
                />
              ) : null}
            </div>

            <div className="flex shrink-0 flex-col items-center overflow-y-auto border-t border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-950/40 lg:border-l lg:border-t-0">
              <WhatsAppDevicePreview
                contactName={recipient.displayName}
                message={devicePreviewProps.message}
                headerText={devicePreviewProps.headerText}
                footer={devicePreviewProps.footer}
                buttons={devicePreviewProps.buttons}
                headerMediaUrl={devicePreviewProps.headerMediaUrl}
                headerMimeType={devicePreviewProps.headerMimeType}
              />
            </div>
          </div>
        )}

        {recipient ? (
          <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm dark:border-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canSend}
              onClick={() => void handleSend()}
              className="rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Send WhatsApp'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DoctorClinicWhatsAppSendModal;
